import { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, Paper, Typography, List, ListItem, Box, Card } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { BakeasyAppBar } from '../../components/common/BakeasyAppBar';
import { t } from 'i18next';
import { BLUE_GRAY_50 } from '../../constants/Colors';
import { BakeasyButtonSend } from '../../components/buttons/BakeasyButton';
import {
  clearMessagesState,
  fetchNewMessageCount,
  getReplies,
  messagesState,
  sendReply
} from '../../redux_store/slices/messagesSlice';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { formatDateTime } from '../../utils/DateUtils';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import BakeasyMessageToList from '../../components/common/BakeasyMessageToList';

export const MessagesThread: FC = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [messages, setMessages] = useState<[]>([]);
  const [sendMessage, setSendMessage] = useState<any>();
  let { selectedValue } = useAppSelector(messagesState);

  const handleSendMessages = async () => {
    if (!sendMessage) {
      let msg = `${t('MESSAGE_IS_REQUIRED')}`;
      dispatch(showErrorSnackbar({ message: msg }));
      return;
    }
    try {
      await dispatch(sendReply({ content: sendMessage, messageId: location?.state?._id }));
      fetchReplies();
      dispatch(fetchNewMessageCount(''));
      setSendMessage('');
    } catch (error) {
      console.error('Error while sending message:', error);
    }
  };

  const fetchReplies = async () => {
    try {
      await dispatch(getReplies({ id: location?.state?._id }));
      setSendMessage('');
    } catch (error) {
      console.error('Error while sending message:', error);
    }
  };

  useEffect(() => {
    if (Array.isArray(selectedValue)) setMessages(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    fetchReplies();
  }, []);

  return (
    <Grid>
      <Grid>
        <Grid item>
          <BakeasyAppBar
            title={t('MESSAGES_THREAD')}
            onBackClick={() => {
              dispatch(clearMessagesState());
              navigate(-1);
            }}
          />
        </Grid>

        {location?.state?.to && (
          <Card sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, p: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <BakeasyMessageToList data={location?.state?.to} />
              </Grid>
            </Grid>
          </Card>
        )}

        <Paper component={Card} variant="outlined" sx={{ mt: 1 }}>
          <List>
            {messages?.length > 0 &&
              messages?.map((message: any, index: any) => (
                <ListItem
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: message.replyStatus === 'Send' ? 'flex-end' : 'flex-start',
                    paddingLeft: message.replyStatus === 'Send' ? '90px' : '16px',
                    paddingRight: message.replyStatus !== 'Send' ? '90px' : '16px',
                    width: '100%' // Ensures the ListItem takes up full available width
                  }}
                >
                  <Box
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: message.replyStatus === 'Send' ? 'white' : BLUE_GRAY_50,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      maxWidth: '100%', // Restrict Box width to avoid overflowing
                      wordWrap: 'break-word' // Break long words that might overflow
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textAlign: message.replyStatus === 'Send' ? 'end' : 'left'
                      }}
                    >
                      {`${message.userName}@${formatDateTime(message.createdDate, true)}`}
                    </Typography>
                    <Typography variant="body2">{message.content}</Typography>
                  </Box>
                </ListItem>
              ))}
          </List>
        </Paper>

        {!location?.state?.disabled && (
          <Card sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, p: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <TextField.ReplyMessage
                  fullWidth
                  value={sendMessage}
                  onChange={event => setSendMessage(event.target.value)}
                />
              </Grid>

              <Grid item>
                <BakeasyButtonSend onClick={handleSendMessages} />
              </Grid>
            </Grid>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};
